import { PermissoesPasta } from '@/core/enums/permissoes-pasta'
import * as z from 'zod'
import { t } from '@lingui/macro'
import { VisibilidadePasta } from './constants'
import {
  GruposUsuarioPermissoes,
  ItemBiblioteca,
  ItemDownload,
  NovaPastaInput
} from '@/core/types/graphql'
import { NovaPastaValues } from '../hooks/use-nova-pasta-dialog'
import { ConcederAcessoValues } from '../hooks/use-conceder-acesso-dialog'
import { FileUploader } from '@/core/utils/file-uploader'
import { FileUploadType } from '@/core/enums/file-upload-type'
import { FileUpload } from '@tecsinapse/cortex-react'
import { Metadata } from '@/modules/biblioteca-virtual/types/types'
import JSZip from 'jszip'
import { saveAs } from 'file-saver'
import { downloadByLink } from '@/core/components/uploader-utils'

const isLeituraEscrita = (current: GruposUsuarioPermissoes | null) =>
  current?.permissaoAcessoPasta?.id === PermissoesPasta.LEITURA_ESCRITA
const isLeitura = (current: GruposUsuarioPermissoes | null) =>
  current?.permissaoAcessoPasta?.id === PermissoesPasta.LEITURA

export const initialValuesFragment = (pastaAtual: ItemBiblioteca) => {
  const { isPasta, permissoes = [], isPublico } = pastaAtual || {}

  if (!isPasta || !permissoes) {
    return {
      isPublico: VisibilidadePasta.PUBLICA,
      idsGrupoAcesso: [],
      idsGrupoAcessoEscrita: []
    }
  }

  const idsGrupoAcesso = permissoes
    .filter(permissao => isLeitura(permissao))
    .map(permissao => ({
      label: permissao?.grupoUsuarios.nome,
      value: permissao?.grupoUsuarios.id,
      favorite: permissao?.grupoUsuarios.favorito
    }))
  const idsGrupoAcessoEscrita = permissoes
    .filter(permissao => isLeituraEscrita(permissao))
    .map(permissao => ({
      label: permissao?.grupoUsuarios.nome,
      value: permissao?.grupoUsuarios.id,
      favorite: permissao?.grupoUsuarios.favorito
    }))

  return {
    isPublico: isPublico
      ? VisibilidadePasta.PUBLICA
      : VisibilidadePasta.PRIVADA,
    idsGrupoAcesso,
    idsGrupoAcessoEscrita
  }
}

export const mapSubmitValues = (
  submitValues: NovaPastaValues | ConcederAcessoValues
): Omit<NovaPastaInput, 'idPai' | 'nome'> => {
  const objLeitura = (submitValues.idsGrupoAcesso ?? []).map(idGrupoAcesso => ({
    id: idGrupoAcesso.value,
    description: PermissoesPasta.LEITURA
  }))

  const objEscrita = (submitValues.idsGrupoAcessoEscrita || []).map(
    idGrupoAcessoEscrita => ({
      id: idGrupoAcessoEscrita.value,
      description: PermissoesPasta.LEITURA_ESCRITA
    })
  )

  const tiposAcesso = submitValues.isPublico?.value
    ? [...objLeitura, ...objEscrita]
    : []

  return {
    idsGrupoAcesso: tiposAcesso.map(tipoAcesso => tipoAcesso.id),
    tiposAcesso: tiposAcesso.map(tipoAcesso => ({
      idGrupoAcesso: tipoAcesso.id,
      permissao: tipoAcesso.description
    }))
  }
}

export const concederAcessoSchema = z
  .object({
    isPublico: z
      .object({
        value: z.boolean(),
        label: z.string()
      })
      .nonoptional({ error: t`O campo Visibilidade é obrigatório` }),
    idsGrupoAcesso: z.array(
      z.object({
        value: z.string(),
        label: z.string(),
        favorite: z.boolean()
      })
    ),
    idsGrupoAcessoEscrita: z.array(
      z.object({
        value: z.string(),
        label: z.string(),
        favorite: z.boolean()
      })
    )
  })
  .refine(
    data => {
      if (data.isPublico.value) {
        return (
          data.idsGrupoAcesso.length > 0 ||
          data.idsGrupoAcessoEscrita.length > 0
        )
      }
      return true
    },
    {
      error: t`É obrigatório escolher uma permissão em pastas públicas.`,
      path: ['isPublico'],
      when: payload =>
        novaPastaValidationSchema
          .pick({
            isPublico: true
          })
          .safeParse(payload.value).success
    }
  )

export const novaPastaValidationSchema = concederAcessoSchema.safeExtend({
  nome: z.string().nonempty({ error: t`O campo Nome é obrigatório.` }),
  idPai: z
    .object({
      value: z.string(),
      label: z.string()
    })
    .optional()
})

export const novaSubPastaSchema = z.object({
  nome: z.string().nonempty({ error: t`O campo Nome é obrigatório.` }),
  idPai: z
    .object({
      value: z.string(),
      label: z.string()
    })
    .optional()
})

export const upload = async (file: FileUpload<Metadata>) => {
  const uploader = new FileUploader(FileUploadType.LIBRARY)

  try {
    await uploader.uploadFromFile(file.file)
    return uploader.representation()
  } catch (e) {
    console.error(e)
    throw e
  }
}

export const downloadFiles = async (
  files?: ItemDownload[],
  singleFile?: boolean
) => {
  if ((files ?? []).length < 1) {
    throw new Error('No files available')
  }

  if (files?.length === 1 && singleFile) {
    downloadByLink(files[0].link)
    return
  }

  const zip = new JSZip()

  const filePromises = (files ?? []).map(async file => {
    try {
      const response = await fetch(file.link)

      if (!response.ok) {
        throw new Error(`Failed to fetch ${file.link}: ${response.statusText}`)
      }

      const fileData = await response.blob()
      zip.file(file.path, fileData, { binary: true })
    } catch (error) {
      console.error(
        `Error processing file ${file.path}:`,
        (error as Error).message
      )
      throw new Error(`Error processing file ${file.path}`)
    }
  })

  await Promise.all(filePromises)

  try {
    const zipBlob = await zip.generateAsync({
      type: 'blob',
      compression: 'STORE'
    })

    saveAs(zipBlob, `download-${new Date().toISOString()}.zip`)
  } catch (error) {
    console.error('Error generating zip file:', (error as Error).message)
    throw new Error(`Error generating zip file: ${(error as Error).message}`)
  }
}
